import { Button, Col, Form, Modal, Nav, Row, Spinner, Tab } from 'react-bootstrap'
import { IMembersNode, IResAddAssignmentGroup, Channel } from '../../../types/assignmentGroups'
import { useMutation, useQuery } from '@apollo/client'
import { GET_ALL_USERS_LIST } from '../../../gql/queries/usersQuery'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { IResAllListUsers } from '../../../types/users'
import { parseId } from '../../../helpers'
import { GET_ALL_ORGANIZATIONS } from '../../../gql/queries/organizationsQuery'
import { useGetUser } from '../../../store/userStore'
import { MultiSelectWithActions } from '../../../components/MultiSelectWithActions'

import * as Yup from 'yup'
import { useFormik } from 'formik'
import { CREATE_GROUP_ASSIGNMENT, UPDATE_GROUP_ASSIGNMENT } from '../../../../modules/assignmentGroupMutation'
import { useGroupChannelConfig } from '../../channelConfig/hooks/useChannelConfig'
import { useGroupChannels } from '../hooks/useGroupChannels'
import { ChannelFormModal } from './ChannelFormModal'
import { DifySection } from '../../channelConfig/components/DifySection'
import { PromptConfigSection } from '../../channelConfig/components/PromptConfigSection'
import { UPDATE_DIFY_CONFIG, UPDATE_PROMPT_CONFIG } from '../../../gql/mutations/channelMutations'

interface IModalGroups {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  infoGroup?: null | {
    id: string
    name: string
    members: IMembersNode[]
    supervisorIds?: (string | number)[]
    organizationId?: string | number
    visibleForTickets?: boolean
  }
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('El nombre es requerido'),
  supervisors: Yup.array().of(Yup.string()),
})

export const ModalAddAssignmentGroup: React.FC<IModalGroups> = ({
  isOpen,
  onClose,
  infoGroup,
  refetch,
}) => {
  const normalizeId = (id?: string | number | null) => (id ? `${parseId(id)}` : '')
  const [activeTab, setActiveTab] = useState<string>('general')

  // Get current user's role and organization
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const currentUser = useGetUser((s) => s.user?.user)
  const isSuperAdministrador = userGroups.includes('SuperAdministrador')
  const isAdmin = userGroups.includes('Administrador') || isSuperAdministrador

  // ── Group Channel Config (Channels Tab) ──
  const groupNumericId = infoGroup ? String(parseId(infoGroup.id)) : undefined
  const { config: groupConfig, loading: loadingGroupConfig, saveGroupConfig } = useGroupChannelConfig(groupNumericId)

  // ── Multi-channel & Dify support ──
  const [updateDifyConfigGql] = useMutation(UPDATE_DIFY_CONFIG)
  const [updatePromptConfigGql] = useMutation(UPDATE_PROMPT_CONFIG)
  const { channels, loading: loadingChannels, saving: savingChannel, saveChannel, deleteChannel } = useGroupChannels(groupNumericId)
  const [channelModalOpen, setChannelModalOpen] = useState(false)
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null)


  const mutationDoc = infoGroup ? UPDATE_GROUP_ASSIGNMENT : CREATE_GROUP_ASSIGNMENT
  const [gqlCreate, { data: dataCreate, loading: loadingCreate, error: errorCreate }] =
    useMutation<IResAddAssignmentGroup>(mutationDoc)



  const [difyForm, setDifyForm] = useState({
    dify_enabled: false,
    dify_api_key: '',
    dify_api_url: 'https://api.dify.ai/v1',
    dify_app_id: '',
    ai_provider: 'DIFY' as 'DIFY' | 'LLM',
    llm_provider: 'ANTHROPIC' as 'ANTHROPIC' | 'OPENAI' | '',
    llm_api_key: '',
    llm_model: '',
    prompt_header: '',
    prompt_body: '',
    prompt_footer: '',
    share_contact_info: null as boolean | null,
    share_quotes: null as boolean | null,
    share_policies: null as boolean | null,
    share_leads: null as boolean | null,
    share_consent_status: null as boolean | null,
  })

  const [savingDify, setSavingDify] = useState(false)

  useEffect(() => {
    if (groupConfig) {
      setDifyForm({
        dify_enabled: groupConfig.dify_enabled || false,
        dify_api_key: groupConfig.dify_api_key || '',
        dify_api_url: groupConfig.dify_api_url || 'https://api.dify.ai/v1',
        dify_app_id: groupConfig.dify_app_id || '',
        ai_provider: groupConfig.ai_provider || 'DIFY',
        llm_provider: groupConfig.llm_provider || 'ANTHROPIC',
        llm_api_key: groupConfig.llm_api_key || '',
        llm_model: groupConfig.llm_model || '',
        prompt_header: groupConfig.prompt_header || '',
        prompt_body: groupConfig.prompt_body || '',
        prompt_footer: groupConfig.prompt_footer || '',
        share_contact_info: groupConfig.share_contact_info ?? null,
        share_quotes: groupConfig.share_quotes ?? null,
        share_policies: groupConfig.share_policies ?? null,
        share_leads: groupConfig.share_leads ?? null,
        share_consent_status: groupConfig.share_consent_status ?? null,
      })
    }
  }, [groupConfig])

  const handleDifyChange = (field: string, value: any) => {
    setDifyForm((prev) => ({ ...prev, [field]: value }))
  }

  const { data: dataUsers, loading: loadingUsers, error } = useQuery<IResAllListUsers>(
    GET_ALL_USERS_LIST,
    { fetchPolicy: 'cache-first' }
  )

  // Query for organizations
  const {
    data: dataOrganizations,
    loading: loadingOrganizations,
    error: errorOrganizations,
  } = useQuery(GET_ALL_ORGANIZATIONS, {
    variables: { first: 100 },
    fetchPolicy: 'no-cache',
  })

  // Filtrar supervisores del listado de usuarios
  const supervisors = useMemo(() => {
    if (!dataUsers?.allUsersList) return []
    return dataUsers.allUsersList.filter((user) => user.primaryGroup?.name === 'Supervisor')
  }, [dataUsers])

  const supervisorOptions = useMemo(
    () =>
      supervisors.map((supervisor) => ({
        value: String(parseId(supervisor.id)),
        label: `${supervisor.firstName} ${supervisor.lastName} - ${supervisor.email}`,
        organizationId: normalizeId(supervisor.organization?.id),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supervisors]
  )

  const memberOptions = useMemo(
    () =>
      (dataUsers?.allUsersList || []).map((user) => {
        const groups =
          user.groupAssignmentSet?.edges?.map(({ node }) => node.name).filter(Boolean) || []
        const groupSuffix = groups.length ? ` | Groups: ${groups.join(', ')}` : ''
        return {
          value: String(parseId(user.id)),
          label: `${user.firstName} ${user.lastName} - ${user.email}${groupSuffix}`,
          name: `${user.firstName} ${user.lastName}`.toLowerCase(),
          email: user.email.toLowerCase(),
          organizationId: normalizeId(user.organization?.id),
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataUsers]
  )

  // Get only members from the current group being edited
  const groupMemberOptions = useMemo(
    () => {
      if (!infoGroup?.members) return memberOptions
      const groupMemberIds = new Set(infoGroup.members.map(({ node }) => String(parseId(node.id))))
      return memberOptions.filter((opt) => groupMemberIds.has(String(opt.value)))
    },
    [infoGroup, memberOptions]
  )

  const formik = useFormik({
    initialValues: {
      name: '',
      user: [] as string[],
      supervisors: [] as string[],
      organizationId: '' as string | number,
      visibleForTickets: false,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const payload = {
        name: values.name,
        userIds: values.user.map((id) => parseId(id)),
        supervisorIds: values.supervisors.map(id => parseId(id)),
        organizationId: values.organizationId ? parseId(values.organizationId) : undefined,
        visibleForTickets: values.visibleForTickets,
      }

      if (infoGroup) {
        gqlCreate({
          variables: {
            groupId: parseId(infoGroup.id),
            groupData: payload,
          },
        })
      } else {
        gqlCreate({
          variables: {
            groupData: payload,
          },
        })
      }
    },
  })

  // Un grupo de asignación pertenece a una organización, así que los usuarios
  // seleccionables tienen que ser de esa misma organización. Para un
  // SuperAdministrador `allUsersList` trae usuarios de todas las organizaciones,
  // por eso hay que filtrar en cuanto hay una organización elegida.
  const selectedOrganizationId = normalizeId(formik.values.organizationId)
  const isFromSelectedOrg = (opt: {organizationId: string}) =>
    !selectedOrganizationId || opt.organizationId === selectedOrganizationId

  const orgMemberIds = useMemo(
    () => new Set(memberOptions.filter(isFromSelectedOrg).map((opt) => opt.value)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [memberOptions, selectedOrganizationId]
  )

  const orgSupervisorOptions = useMemo(
    () => supervisorOptions.filter(isFromSelectedOrg),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supervisorOptions, selectedOrganizationId]
  )

  // Al cambiar de organización (solo un SuperAdministrador puede) se descartan
  // los seleccionados que ya no pertenecen a ella, para no guardar miembros de
  // otra organización sin que se vean en la lista.
  const handleOrganizationChange = (value: string) => {
    formik.setFieldValue('organizationId', value)
    const org = normalizeId(value)
    if (!org) return
    const keep = (id: string) => {
      const opt = memberOptions.find((o) => o.value === String(parseId(id)))
      return !opt || opt.organizationId === org
    }
    formik.setFieldValue('user', formik.values.user.filter(keep))
    formik.setFieldValue('supervisors', formik.values.supervisors.filter(keep))
  }

  const selectedSupervisorOptions = useMemo(() => {
    const selectedIds = (formik.values.supervisors || []).map((id) => String(parseId(id)))
    return supervisorOptions.filter((opt) => selectedIds.includes(String(opt.value)))
  }, [formik.values.supervisors, supervisorOptions])

  const [searchAvailableMembers, setSearchAvailableMembers] = useState('')
  const [searchAssignedMembers, setSearchAssignedMembers] = useState('')
  const [sortBy, setSortBy] = useState<'name' | 'email'>('name')

  const sortOptions = (list: typeof memberOptions) =>
    [...list].sort((a, b) => a[sortBy].localeCompare(b[sortBy]))

  const filteredAvailableMembers = useMemo(() => {
    const assignedIds = new Set(formik.values.user)
    const searchLower = searchAvailableMembers.toLowerCase()
    const filtered = memberOptions.filter(
      (opt) =>
        !assignedIds.has(opt.value) &&
        orgMemberIds.has(opt.value) &&
        opt.label.toLowerCase().includes(searchLower)
    )
    return sortOptions(filtered)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberOptions, orgMemberIds, formik.values.user, searchAvailableMembers, sortBy])

  const filteredAssignedMembers = useMemo(() => {
    const assignedIds = new Set(formik.values.user)
    const searchLower = searchAssignedMembers.toLowerCase()
    const filtered = memberOptions.filter(
      (opt) => assignedIds.has(opt.value) && opt.label.toLowerCase().includes(searchLower)
    )
    return sortOptions(filtered)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberOptions, formik.values.user, searchAssignedMembers, sortBy])

  useEffect(() => {
    setSearchAvailableMembers('')
    setSearchAssignedMembers('')
  }, [isOpen])

  useEffect(() => {
    if (!infoGroup) {
      // Set organization from current user if not SuperAdministrador
      if (!isSuperAdministrador && currentUser?.organization?.id) {
        formik.setFieldValue('organizationId', normalizeId(currentUser.organization.id))
      }
      return
    }
    formik.setValues({
      name: infoGroup.name,
      user: infoGroup.members.map(({ node }) => String(parseId(node.id))),
      supervisors: infoGroup.supervisorIds?.map((id) => String(parseId(id))) || [],
      organizationId: infoGroup.organizationId || (currentUser?.organization?.id ? normalizeId(currentUser.organization.id) : ''),
      visibleForTickets: Boolean(infoGroup.visibleForTickets),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoGroup])

  useEffect(() => {
    if (error) toast.error('Error: ' + error.message)
  }, [error])

  useEffect(() => {
    if (errorOrganizations) toast.error('Error loading organizations: ' + errorOrganizations.message)
  }, [errorOrganizations])

  useEffect(() => {
    if (!dataCreate) return
    const payload = dataCreate.createGroupAssignmentMutation || dataCreate.updateGroupAssignmentMutation
    if (payload?.groupAssignment?.id) {
      const savedGroupId = normalizeId(payload.groupAssignment.id)

      // If we are editing or creating, we can save Dify config now
      if (savedGroupId) {
        setSavingDify(true)
        saveGroupConfig(difyForm)
          .then((success) => {
            if (success && success.ok) {
              toast.success(infoGroup ? 'Assignment group and Dify config updated successfully' : 'Assignment group created successfully')
            } else {
              toast.warning('Group saved, but Dify configuration failed to update')
            }
          })
          .catch((err) => {
            console.error(err)
            toast.error('Error saving group Dify config')
          })
          .finally(() => {
            setSavingDify(false)
            refetch()
            onClose()
          })
      } else {
        toast.success(infoGroup ? 'Assignment group updated successfully' : 'Assignment group created successfully')
        refetch()
        onClose()
      }
    } else if (payload?.errors?.length) {
      toast.error(
        `Error ${payload.errors[0]?.message || 'Something happened'
        }`
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCreate])

  useEffect(() => {
    if (errorCreate) toast.error('Error: ' + errorCreate.message)
  }, [errorCreate])

  // Webhook URL for display

  return (
    <Modal show={isOpen} onHide={onClose} size='xl' centered>
      <Modal.Header closeButton>
        <Modal.Title>{infoGroup ? `Edit ${infoGroup.name}` : 'Add Assignment Group'}</Modal.Title>
      </Modal.Header>

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'general')}>
        <Modal.Body style={{ minHeight: '56vh', paddingTop: 0 }}>
          <Nav variant='tabs' className='mb-4 mt-3'>
            <Nav.Item>
              <Nav.Link eventKey='general'>
                <i className='bi bi-info-circle me-1' />
                General Info
              </Nav.Link>
            </Nav.Item>
            {infoGroup && (
              <Nav.Item>
                <Nav.Link eventKey='channels'>
                  <i className='bi bi-telephone-forward me-1' />
                  Communication Channels
                  {groupConfig?.whatsapp_provider && groupConfig.whatsapp_provider !== 'NONE' && (
                    <i className='bi bi-whatsapp text-success ms-2' />
                  )}
                </Nav.Link>
              </Nav.Item>
            )}
            {infoGroup && (
              <Nav.Item>
                <Nav.Link eventKey='dify'>
                  <i className='bi bi-robot me-1' />
                  AI Integration
                  {difyForm.dify_enabled && (
                    <i className='bi bi-circle-fill text-success ms-2' style={{ fontSize: '6px', verticalAlign: 'middle' }} />
                  )}
                </Nav.Link>
              </Nav.Item>
            )}
          </Nav>

          <Tab.Content>
            {/* ── General Info Tab ── */}
            <Tab.Pane eventKey='general'>
              {loadingUsers || !dataUsers ? (
                <div className='d-flex align-items-center justify-content-center' style={{ height: '200px' }}>
                  <Spinner animation='border' />
                </div>
              ) : (
                <Form id='general-form' onSubmit={formik.handleSubmit}>
                  <Row className='g-3 align-items-end'>
                    <Col md={5}>
                      <Form.Group controlId='name'>
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          placeholder='Enter Group name'
                          value={formik.values.name}
                          onChange={formik.handleChange}
                          isInvalid={Boolean(formik.touched.name && formik.errors.name)}
                        />
                        <Form.Control.Feedback type='invalid'>{formik.errors.name}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group controlId='organizationId'>
                        <Form.Label>Organization</Form.Label>
                        <Form.Select
                          name='organizationId'
                          value={formik.values.organizationId}
                          onChange={(e) => handleOrganizationChange(e.target.value)}
                          isInvalid={Boolean(formik.errors.organizationId && formik.touched.organizationId)}
                          disabled={!isSuperAdministrador || loadingOrganizations}
                        >
                          {loadingOrganizations ? (
                            <option value=''>Loading...</option>
                          ) : (
                            <option value=''>Select One</option>
                          )}
                          {dataOrganizations?.allOrganizations?.edges?.map(({ node }: any) => {
                            const value = normalizeId(node.id)
                            return (
                              <option key={node.id} value={value}>
                                {node.name}
                              </option>
                            )
                          })}
                        </Form.Select>
                        <Form.Control.Feedback type='invalid'>
                          {formik.errors.organizationId}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group controlId='visibleForTickets' className='mb-1'>
                        <Form.Check
                          type='checkbox'
                          name='visibleForTickets'
                          label='Visible for tickets'
                          checked={Boolean(formik.values.visibleForTickets)}
                          onChange={formik.handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className='g-3 mt-1'>
                    <Col md={4}>
                      <Form.Group controlId='supervisors'>
                        <Form.Label>Supervisors (optional)</Form.Label>
                        <MultiSelectWithActions
                          value={selectedSupervisorOptions}
                          options={orgSupervisorOptions}
                          onChange={(selected) => {
                            const values = Array.isArray(selected)
                              ? selected.map((opt: any) => String(opt?.value || '').trim()).filter(Boolean)
                              : []
                            formik.setFieldValue('supervisors', values)
                          }}
                        />
                        {orgSupervisorOptions.length === 0 && (
                          <Form.Text className='text-muted'>
                            No supervisors available. Only users with "Supervisor" role from the
                            selected organization will appear here.
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className='mt-4'>
                    <div className='d-flex align-items-center justify-content-between mb-2'>
                      <Form.Label className='mb-0'>Members</Form.Label>
                      <div className='d-flex align-items-center gap-2'>
                        <span className='text-muted fs-7'>Sort by:</span>
                        <div className='btn-group btn-group-sm'>
                          <button
                            type='button'
                            className={`btn btn-sm ${sortBy === 'name' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setSortBy('name')}
                          >
                            Name
                          </button>
                          <button
                            type='button'
                            className={`btn btn-sm ${sortBy === 'email' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setSortBy('email')}
                          >
                            Email
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className='d-flex w-100 flex-row gap-3'>
                      <div className='list-user-available w-50 p-3 border border-secondary rounded'>
                        <div className='title'>Members available</div>
                        <Form.Control
                          type='text'
                          placeholder='Search by name or email...'
                          value={searchAvailableMembers}
                          onChange={(e) => setSearchAvailableMembers(e.target.value)}
                          className='mb-3'
                        />
                        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          {filteredAvailableMembers.map((opt) => (
                            <div
                              key={opt.value}
                              className='user-available'
                              style={{ cursor: 'pointer' }}
                              onClick={() =>
                                formik.setFieldValue('user', [...formik.values.user, opt.value])
                              }
                            >
                              {opt.label}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className='list-user-assigned w-50 p-3 border border-secondary rounded'>
                        <div className='title'>Members assigned</div>
                        <Form.Control
                          type='text'
                          placeholder='Search by name or email...'
                          value={searchAssignedMembers}
                          onChange={(e) => setSearchAssignedMembers(e.target.value)}
                          className='mb-3'
                        />
                        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          {filteredAssignedMembers.map((opt) => (
                            <div
                              key={opt.value}
                              className='user-available'
                              style={{ cursor: 'pointer' }}
                              onClick={() =>
                                formik.setFieldValue(
                                  'user',
                                  formik.values.user.filter((id) => id !== opt.value)
                                )
                              }
                            >
                              {opt.label}{' '}
                              <span className='text-danger ms-1 fw-bold'>✕</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                </Form>
              )}
            </Tab.Pane>

            {/* ── Communication Channels Tab ── */}
            {infoGroup && (
              <Tab.Pane eventKey='channels'>
                {loadingChannels ? (
                  <div className='d-flex justify-content-center align-items-center' style={{ height: '200px' }}>
                    <Spinner animation='border' variant='primary' />
                  </div>
                ) : (
                  <div>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <h6 className='fw-bold mb-0'>
                        <i className='bi bi-telephone-forward me-2' />
                        Communication Channels
                      </h6>
                      {isAdmin && (
                        <Button
                          variant='primary'
                          size='sm'
                          onClick={() => {
                            setEditingChannel(null)
                            setChannelModalOpen(true)
                          }}
                        >
                          <i className='bi bi-plus-lg me-1' /> Add Channel
                        </Button>
                      )}
                    </div>

                    {channels.length === 0 ? (
                      <div className='text-muted text-center py-5'>
                        <p>No channels configured yet.</p>
                        {isAdmin && <p className='small'>Click "Add Channel" to get started.</p>}
                      </div>
                    ) : (
                      <div className='table-responsive'>
                        <table className='table table-sm table-hover align-middle'>
                          <thead>
                            <tr>
                              <th>Type</th>
                              <th>Provider</th>
                              <th>Phone Number</th>
                              <th>Assigned Members</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {channels.map((ch) => (
                              <tr key={ch.id}>
                                <td>
                                  <span className='badge bg-secondary'>{ch.channel_type}</span>
                                </td>
                                <td>{ch.provider}</td>
                                <td>
                                  <code>{ch.phone_number || '—'}</code>
                                </td>
                                <td>
                                  {ch.assigned_member_ids.length === 0 ? (
                                    <span className='text-muted small'>All members</span>
                                  ) : (
                                    <span>{ch.assigned_member_ids.length} member(s)</span>
                                  )}
                                </td>
                                <td>
                                  <span className={`badge ${ch.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                    {ch.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td>
                                  {isAdmin && (
                                    <>
                                      <Button
                                        variant='outline-secondary'
                                        size='sm'
                                        className='me-1'
                                        onClick={() => {
                                          setEditingChannel(ch)
                                          setChannelModalOpen(true)
                                        }}
                                      >
                                        <i className='bi bi-pencil' />
                                      </Button>
                                      <Button
                                        variant='outline-danger'
                                        size='sm'
                                        onClick={async () => {
                                          if (window.confirm('Delete this channel?')) {
                                            const ok = await deleteChannel(ch.id!)
                                            if (ok) {
                                              toast.success('Channel deleted')
                                            } else {
                                              toast.error('Error deleting channel')
                                            }
                                          }
                                        }}
                                      >
                                        <i className='bi bi-trash' />
                                      </Button>
                                    </>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {!isAdmin && (
                      <div className='alert alert-info mt-3 mb-0'>
                        <i className='bi bi-info-circle me-2' />
                        Only Administrators can modify channel configuration.
                      </div>
                    )}
                  </div>
                )}

                <ChannelFormModal
                  isOpen={channelModalOpen}
                  onClose={() => setChannelModalOpen(false)}
                  onSave={async (data) => {
                    try {
                      const saved = await saveChannel(data)
                      if (saved) {
                        toast.success('Channel saved')
                      }
                      return saved as any
                    } catch (error: any) {
                      const errorMessage = error?.message || 'Error saving channel'
                      toast.error(errorMessage)
                      throw error
                    }
                  }}
                  saving={savingChannel}
                  initialData={editingChannel}
                  groupMembers={groupMemberOptions}
                />
              </Tab.Pane>
            )}
            {infoGroup && (
              <Tab.Pane eventKey='dify'>
                {loadingGroupConfig ? (
                  <div className='d-flex align-items-center justify-content-center py-5'>
                    <Spinner animation='border' variant='primary' className='me-2' />
                    <span>Loading Dify Configuration...</span>
                  </div>
                ) : (
                  <Form id='dify-form' onSubmit={async (e) => {
                    e.preventDefault()
                    setSavingDify(true)
                    try {
                      const res = await updateDifyConfigGql({
                        variables: {
                          scope: 'group',
                          targetId: groupNumericId ? parseInt(groupNumericId, 10) : undefined,
                          difyEnabled: difyForm.dify_enabled,
                          difyApiKey: difyForm.dify_api_key,
                          difyApiUrl: difyForm.dify_api_url,
                          difyAppId: difyForm.dify_app_id,
                          aiProvider: difyForm.ai_provider,
                          llmProvider: difyForm.llm_provider,
                          llmApiKey: difyForm.llm_api_key,
                          llmModel: difyForm.llm_model,
                        },
                      })
                      if (res.data?.updateDifyConfig?.success) {
                        toast.success('Dify configuration saved successfully')
                        refetch()
                      } else {
                        toast.error(`Error saving Dify config: ${res.data?.updateDifyConfig?.error || 'Unknown error'}`, { autoClose: 8000 })
                      }

                      await updatePromptConfigGql({
                        variables: {
                          scope: 'group',
                          targetId: groupNumericId ? parseInt(groupNumericId, 10) : undefined,
                          promptBody: difyForm.prompt_body,
                          shareContactInfo: difyForm.share_contact_info,
                          shareQuotes: difyForm.share_quotes,
                          sharePolicies: difyForm.share_policies,
                          shareLeads: difyForm.share_leads,
                          shareConsentStatus: difyForm.share_consent_status,
                        },
                      })
                    } catch (err: any) {
                      toast.error('Error saving Dify config: ' + (err?.message || err))
                    } finally {
                      setSavingDify(false)
                    }
                  }}>
                    <DifySection
                      difyEnabled={difyForm.dify_enabled}
                      difyApiKey={difyForm.dify_api_key}
                      difyApiUrl={difyForm.dify_api_url}
                      difyAppId={difyForm.dify_app_id}
                      onDifyEnabledChange={(val) => handleDifyChange('dify_enabled', val)}
                      onDifyApiKeyChange={(val) => handleDifyChange('dify_api_key', val)}
                      onDifyApiUrlChange={(val) => handleDifyChange('dify_api_url', val)}
                      onDifyAppIdChange={(val) => handleDifyChange('dify_app_id', val)}
                      organizationId={normalizeId(formik.values.organizationId)}
                      aiProvider={difyForm.ai_provider}
                      onAiProviderChange={(val) => handleDifyChange('ai_provider', val)}
                      llmProvider={difyForm.llm_provider}
                      onLlmProviderChange={(val) => handleDifyChange('llm_provider', val)}
                      llmApiKey={difyForm.llm_api_key}
                      onLlmApiKeyChange={(val) => handleDifyChange('llm_api_key', val)}
                      llmModel={difyForm.llm_model}
                      onLlmModelChange={(val) => handleDifyChange('llm_model', val)}
                    />
                    <PromptConfigSection
                      isOrgScope={false}
                      promptHeader={difyForm.prompt_header}
                      onPromptHeaderChange={(val) => handleDifyChange('prompt_header', val)}
                      promptBody={difyForm.prompt_body}
                      onPromptBodyChange={(val) => handleDifyChange('prompt_body', val)}
                      promptFooter={difyForm.prompt_footer}
                      onPromptFooterChange={(val) => handleDifyChange('prompt_footer', val)}
                      shareContactInfo={difyForm.share_contact_info}
                      onShareContactInfoChange={(val) => handleDifyChange('share_contact_info', val)}
                      shareQuotes={difyForm.share_quotes}
                      onShareQuotesChange={(val) => handleDifyChange('share_quotes', val)}
                      sharePolicies={difyForm.share_policies}
                      onSharePoliciesChange={(val) => handleDifyChange('share_policies', val)}
                      shareLeads={difyForm.share_leads}
                      onShareLeadsChange={(val) => handleDifyChange('share_leads', val)}
                      shareConsentStatus={difyForm.share_consent_status}
                      onShareConsentStatusChange={(val) => handleDifyChange('share_consent_status', val)}
                    />
                    <div className='d-flex justify-content-end mt-4 pt-3 border-top'>
                      <Button
                        type='submit'
                        variant='primary'
                        disabled={savingDify}
                        className='d-flex align-items-center gap-2'
                      >
                        {savingDify && <Spinner size='sm' animation='border' />}
                        Save Dify Settings
                      </Button>
                    </div>
                  </Form>
                )}
              </Tab.Pane>
            )}
          </Tab.Content>
        </Modal.Body>

        <Modal.Footer>
          <Button type='button' variant='secondary' onClick={onClose}>
            Close
          </Button>
          {activeTab === 'general' && (
            <Button
              type='submit'
              form='general-form'
              variant='primary'
              disabled={loadingCreate}
              className='d-flex align-items-center gap-2'
            >
              {loadingCreate && <Spinner size='sm' animation='border' />}
              Save Changes
            </Button>
          )}
        </Modal.Footer>
      </Tab.Container>
    </Modal>
  )
}
